import React, { Fragment, useState } from 'react';
import styled from 'styled-components';
import { TextField } from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import { State, WorkflowInvoke } from '@sierralabs/workflow';

import { compareTaskStatus, parseTaskStatus } from '../../../tasks/task.helper';
import {
  CreateSubTaskParams,
  RemoveSubTasksParams,
  TaskContext
} from '../../../tasks/Task.machine';
import {
  ServiceConfiguratorProps,
  ServiceDisplayProps,
  ServicePlugin
} from '../ServicePlugin';
import { CreateSubTasksPlugin } from './CreateSubTasksPlugin';

const StyledConfiguratorDiv = styled.div`
  .field {
    margin-top: 12px;
    margin-bottom: 12px;
  }
`;

const RemoveSubTasksConfigurator: React.FC<
  ServiceConfiguratorProps
> = props => {
  const { stateNode, onChange } = props;
  const [invoke, setInvoke] = useState(props.invoke);
  if (!invoke.data) invoke.data = {};
  const data: RemoveSubTasksParams['data'] = invoke.data;
  const states = stateNode.parent?.states || [];
  const selectedStates = states.filter(state => {
    return data.statePaths?.some(statePath => {
      return compareTaskStatus(statePath, state.path);
    });
  });

  const update = (data: RemoveSubTasksParams['data']) => {
    const updatedInvoke = { ...invoke, data };
    setInvoke(updatedInvoke);
    onChange?.(updatedInvoke);
  };

  return (
    <StyledConfiguratorDiv>
      <Autocomplete
        multiple
        fullWidth
        className='field'
        options={states}
        value={selectedStates}
        getOptionLabel={option => option.name}
        // use filterOptions instead of filtering the options
        // to allow invalid values in selectedStates
        // e.g. CreateSubTasks plugin was removed from a state
        filterOptions={states => {
          // only get states that have CreateSubTasks actions
          return states.filter(state => {
            const invokes = Array.isArray(state.invoke)
              ? state.invoke
              : state.invoke
              ? [state.invoke]
              : [];
            return invokes.some((invoke): invoke is CreateSubTaskParams => {
              return invoke.src === CreateSubTasksPlugin.src;
            });
          });
        }}
        getOptionSelected={(option, value) => {
          return compareTaskStatus(option.path, value.path);
        }}
        onChange={(_, states) => {
          update({ statePaths: states.map(state => state.path) });
        }}
        renderInput={params => (
          <TextField
            {...params}
            fullWidth
            size='small'
            variant='outlined'
            label='Workflow States'
            placeholder='Remove Sub Tasks from selected states'
            helperText={
              <>
                Only the Workflow states with a configured{' '}
                <strong>{CreateSubTasksPlugin.name}</strong> action can be
                selected.
              </>
            }
          />
        )}
      />

      <Alert severity='info'>
        <div>
          {(data.statePaths || []).length > 0 ? (
            <>
              This action will remove only the assigned sub tasks created from
              the selected <strong>workflow states</strong> when entering into
              this workflow state.
            </>
          ) : (
            'This action will remove any assigned sub tasks when entering into this workflow state.'
          )}
        </div>
      </Alert>
    </StyledConfiguratorDiv>
  );
};

const RemoveSubTasksDisplay: React.FC<ServiceDisplayProps> = props => {
  const { invoke } = props;
  const data: RemoveSubTasksParams['data'] | undefined = invoke.data;
  const statePaths = data?.statePaths || [];
  return statePaths.length === 0 ? null : (
    <div>
      for:{' '}
      {statePaths.map((statePath, index, array) => (
        <Fragment key={`${statePath}-${index}`}>
          <strong>{parseTaskStatus(statePath)}</strong>
          {index < array.length - 1 && ', '}
        </Fragment>
      ))}
    </div>
  );
};

export const RemoveSubTasksPlugin: ServicePlugin<RemoveSubTasksParams> = {
  src: 'removeSubTasks',
  name: 'Remove Sub Tasks',
  configurator: RemoveSubTasksConfigurator,
  display: RemoveSubTasksDisplay,
  validate: (_condition: Partial<WorkflowInvoke>) => {
    return true;
  },
  service: (state: State<TaskContext>, params) => {
    console.log('removeSubTasks params', state, params);
    return true;
  }
};
